import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronRight, Search, Plus, Minus, ChevronUp } from "lucide-react";
import { ReactComponent as FAQ } from "../../../assets/icons/svg/FAQ.svg";
import { ReactComponent as SupportIcon } from "../../../assets/icons/svg/support.svg";
import { ReactComponent as PastQuery } from "../../../assets/icons/svg/post_quries.svg";
import ToastContainer, { useToast } from "../../ui/Toast_Message";


// Type definitions
interface FormData {
  name: string;
  phone: string;
  reason: string;
  message: string;
}

interface SupportFormProps {
  onSubmit?: (formData: FormData) => void;
}

interface FAQ {
  id: string;
  question: string;
  answer: string;
  isExpanded: boolean;
}

interface Query {
  id: string;
  question: string;
  date: string;
  status: string;
}

type TabType = "support" | "faqs" | "past";

// SupportForm Component
const SupportForm: React.FC<SupportFormProps> = ({ onSubmit }) => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    phone: "",
    reason: "",
    message: "",
  });

  const [reasonDropdownOpen, setReasonDropdownOpen] = useState<boolean>(false);

  const { toasts, showToast } = useToast();

  const reasons: string[] = [
    "Flow doubt",
    "Technical issue",
    "Feature request",
    "Billing inquiry",
    "General question",
  ];

  const handleInputChange = (field: keyof FormData, value: string): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isFormValid = (): boolean => {
    return !!(
      formData.name.trim() &&
      formData.phone.trim() &&
      formData.phone !== "+91" &&
      formData.reason.trim() &&
      formData.message.trim()
    );
  };

  const handleSubmit = (): void => {
    if (isFormValid()) {
      // Show success toast
      showToast(
        "success",
        "Form Submitted!",
        "Your support request has been sent successfully.",
        `form-submit-${Date.now()}` // Unique key to prevent duplicate toasts
      );
      
      // Call the onSubmit callback if provided
      onSubmit?.(formData);
      
      // Reset form after submission
      setFormData({
        name: "",
        phone: "",
        reason: "",
        message: "",
      });
    } else {
      // Show error toast if form is invalid
      showToast(
        "error",
        "Form Invalid",
        "Please fill in all required fields before submitting.",
        `form-error-${Date.now()}`
      );
    }
  };

  return (
    <div className="space-y-6">
      <ToastContainer toasts={toasts} />
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Name*:
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter your name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone number*:
          </label>
          <input
            type="text"
            value={formData.phone}
            onChange={(e) => handleInputChange("phone", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="+91"
          />
        </div>

        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reason*
          </label>
          <button
            onClick={() => setReasonDropdownOpen(!reasonDropdownOpen)}
            className="w-full px-3 py-2 border border-gray-300 rounded-[8px] text-left text-sm bg-white flex items-center justify-between focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            <span
              className={formData.reason ? "text-black" : "text-gray-400"}
            >
              {formData.reason || "Select reason"}
            </span>
            <span>{reasonDropdownOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
          </button>

          {reasonDropdownOpen && (
            <div className="absolute z-10 mt-2 w-full rounded-lg bg-white border border-gray-200 shadow-lg">
              {reasons.map((reason) => (
                <div
                  key={reason}
                  onClick={() => {
                    handleInputChange("reason", reason);
                    setReasonDropdownOpen(false);
                  }}
                  className={`px-4 py-2 cursor-pointer rounded-md mx-1 my-1 hover:bg-blue-50 text-sm ${
                    formData.reason === reason
                      ? "bg-blue-100 font-medium"
                      : "text-gray-700"
                  }`}
                >
                  {reason}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Message*:
        </label>
        <textarea
          value={formData.message}
          onChange={(e) => handleInputChange("message", e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          placeholder="Add your questions, feedback, suggestions. Feel free to add as many details as you can"
        />
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={!isFormValid()}
          className={`px-6 py-2 rounded-md font-medium transition-colors ${
            isFormValid()
              ? "bg-[#0BA6C7] hover:bg-[#0A99B9] text-white"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          Send
        </button>
      </div>
    </div>
  );
};

// FAQs Component
const FAQs: React.FC = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([
    {
      id: "1",
      question:
        "What is digital signage, and how does it work?",
      answer:
        "Digital signage is a form of electronic display that shows multimedia content such as images, videos, and information on digital screens. These screens are typically managed via a CMS, which allows users to control, schedule, and update content remotely. The system connects the digital screen to a CMS via an internet connection.",
      isExpanded: false,
    },
    {
      id: "2",
      question: "What is a CMS for digital signage?",
      answer: "A CMS (Content Management System) for digital signage is software that allows users to manage and schedule content displayed on digital screens. Users can upload images, videos, and other media through the CMS, create playlists, schedule content, and monitor displays remotely.",
      isExpanded: false,
    },
    {
      id: "3",
      question:
        "How much does a digital signage system cost?",
      answer: "The cost of digital signage depends on various factors: The number and size of screens Hardware (media players, mounting systems) Software or CMS annual subscription fees Installation and maintenance costs Content creation (in-house or outsourced) Many vendors, such as Acumen CMS, offer cost-effective packages for businesses of all sizes.",
      isExpanded: false,
    },
    {
      id: "4",
      question:
        "Is it difficult to update or change the content?",
      answer: "Not at all! With a good CMS, like Acumen CMS, updating or changing content is simple and user-friendly. Most systems are designed with drag-and-drop functionality, allowing you to upload new content, schedule it, and preview it in just a few clicks.",
      isExpanded: false,
    },
    {
      id: "5",
      question:
        "Can I control multiple screens from one CMS?",
      answer: "Yes, a CMS allows you to manage multiple screens from a single platform. You can schedule different content for different screens or apply the same content across all screens. Acumen CMS allows for remote management, meaning you can control screens from any location.",
      isExpanded: false,
    },
    {
      id: "6",
      question:
        "What kind of content can be displayed on digital signage?",
      answer: "You can display a wide range of content, including: Images (JPEG, PNG) Videos (MP4) Live feeds (Youtube streams)",
      isExpanded: false,
    },
    {
      id: "7",
      question: "Do I need special hardware to run digital signage?",
      answer: "Yes, digital signage typically requires: A display screen (commercial-grade recommended for durability) A media player (to stream content to the screen) An internet connection (for remote CMS management) Optional accessories (mounting hardware)",
      isExpanded: false,
    },
    {
      id: "8",
      question: "How reliable is digital signage for 24/7 operation?",
      answer: "Commercial-grade screens and reliable media players are designed for 24/7 operation. Paired with a robust CMS, your digital signage should run continuously without issues. However, choosing high-quality hardware is crucial for long-term reliability.",
      isExpanded: false,
    },
    
  ]);

  const toggleFAQ = (id: string): void => {
    setFaqs((prev) =>
      prev.map((faq) =>
        faq.id === id ? { ...faq, isExpanded: !faq.isExpanded } : faq
      )
    );
  };

  return (
    <div className="space-y-4">
      {faqs.map((faq) => (
        <div key={faq.id} className="bg-white rounded-lg shadow-md py-1 px-4">
          <button
            onClick={() => toggleFAQ(faq.id)}
            className="w-full flex items-center justify-between py-4 text-left"
          >
            <span className="text-sm font-medium text-gray-900 pr-4">
              {faq.question}
            </span>
            <div className="flex items-center flex-shrink-0">
              <div className="w-6 h-6 border border-gray-300 rounded flex items-center justify-center bg-white">
                {faq.isExpanded ? (
                  <Minus className="w-3 h-3 text-gray-600" />
                ) : (
                  <Plus className="w-3 h-3 text-gray-600" />
                )}
              </div>
            </div>
          </button>
          {faq.isExpanded && faq.answer && (
            <div className="pb-4 text-sm text-gray-600 leading-relaxed">
              <div className=" p-4 rounded-md">{faq.answer}</div>
            </div>
          )}
          {faq.isExpanded && !faq.answer && (
            <div className="pb-4 text-sm text-gray-500 italic">
              <div className=" p-4 rounded-md">
                No answer available for this question yet.
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// PastQueries Component
const PastQueries: React.FC = () => {
  const pastQueries: Query[] = [
    {
      id: "1",
      question: "How to create playlist?",
      date: "20 Mar, 2025",
      status: "answered",
    },
    {
      id: "2",
      question: "How to create playlist?",
      date: "20 Mar, 2025",
      status: "answered",
    },
   
  ];

  return (
    <div className="space-y-4">
      {pastQueries.map((query) => (
        <div
          key={query.id}
          className="flex items-center justify-between py-3 px-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
        >
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-gray-900 truncate">
              {query.question}
            </h3>
            <div className="mt-1 flex items-center space-x-2">
              <span className="text-xs text-gray-500">{query.date}</span>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Answered
              </span>
            </div>
          </div>
          <div className="flex items-center ml-4">
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </div>
        </div>
      ))}
    </div>
  );
};

// Main Support Component
const Support: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>("support");
  const [search, setSearch] = useState<string>("");

  const handleFormSubmit = (formData: FormData): void => {
    console.log("Form submitted:", formData);
  };

  const renderTabContent = (): React.ReactNode => {
    switch (activeTab) {
      case "support":
        return <SupportForm onSubmit={handleFormSubmit} />;
      case "faqs":
        return <FAQs />;
      case "past":
        return <PastQueries />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className=" mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with search */}
        <div className="flex items-center justify-between pt-6 pb-8">
          <h1 className="text-2xl font-semibold text-gray-900"></h1>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              className="w-64 pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="flex space-x-1 ">
            <button
              onClick={() => setActiveTab("support")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === "support"
                  ? "border-[0.5px] border-[#0BA6C7] bg-[#F2FDFF] text-[#1F1F1F] shadow-sm"
                  : ""
              }`}
            >
              <div className="flex items-center gap-2">
                <SupportIcon className="w-5 h-5" />
                <span>Support form </span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab("faqs")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === "faqs"
                  ? "border-[0.5px] border-[#0BA6C7] bg-[#F2FDFF] text-[#1F1F1F] shadow-sm"
                  : ""
              }`}
            >
              <div className="flex items-center gap-2">
                <FAQ className="w-5 h-5" />
                <span> FAQs </span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab("past")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === "past"
                  ? "border-[0.5px] border-[#0BA6C7] bg-[#F2FDFF] text-[#1F1F1F] shadow-sm"
                  : ""
              }`}
            >
              <div className="flex items-center gap-2">
                <PastQuery className="w-5 h-5" />
                <span> Past queries </span>
              </div>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="pb-8">{renderTabContent()}</div>
      </div>
    </div>
  );
};

export default Support;